import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Typography } from "@mui/material";
import { useUploadStore } from "@components/FileUpload/uploadStore.ts";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import StepperControls from "@components/StepperControls/StepperControls.tsx";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection";
import { useNotification } from "@context/NotificationContext.tsx";
import { useCheckOwnersMatter } from "@features/vessel-call/vessel-call-status/hooks/useCheckOwnersMatter.ts";
import {
  createMovementType,
  getMoveTypeForLocation,
  mapFromToLocationsToMovementOptions,
  type MovementLocationOption,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { MovementOutletContext } from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer.tsx";
import {
  movementServiceFormDefaultValues,
  useMovementServiceFormStore,
} from "@features/vessel-call/vessel-call-status/MovementServices/store/useMovementServiceForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store.ts";
import { getMovementServiceDetailsById } from "@services/movementServicesApi.ts";
import { getAnchorageAreas, REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import type { FromToLocation } from "@services/vessel-call/types.ts";
import { useQuery } from "@tanstack/react-query";
import { checkPermission } from "@utils/user-permissions.tsx";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import type { MovementServiceDetailsBackendResponse } from "../preview/types";
import {
  H2S_VISIBLE_DESTINATIONS,
  isMarineRequiredFieldsMovementType,
  MOVEMENT_SERVICE_REMARKS_MAX_CHARS,
  type MovementServiceFormData,
} from "../schema/movementServiceFormSchema";
import AdditionalResources from "./AdditionalResources";
import H2S from "./H2S";
import MovementAdditionalDocuments from "./MovementAdditionalDocuments";
import MovementVesselDetails from "./MovementVesselDetails";
import PilotRequestNotification from "./PilotRequestNotification";

const LANG_BASE = "vessel-call.movement-services";
const uploadFieldKeys = [
  "additionalDocuments",
  "pilotRequestNotification",
  "h2s",
] as const satisfies readonly (keyof MovementServiceFormData)[];
const uploadFieldSet = new Set<UploadField>(uploadFieldKeys);

type UploadField = (typeof uploadFieldKeys)[number];

const SERVICE = "bff-vessel-call";
const DEFAULT_FROM_VALUE = "SEA";

const DOCUMENT_TYPE_FALLBACKS: Record<UploadField, string> = {
  additionalDocuments: "ADDITIONAL DOCUMENTS",
  pilotRequestNotification: "PILOT REQUEST NOTIFICATION",
  h2s: "H2S",
};

type UploadEntry = NonNullable<MovementServiceFormData["additionalDocuments"]>[number];

const mapDocumentsToFormEntries = (
  docs: MovementServiceDetailsBackendResponse[UploadField] = [],
  fallbackType: string,
): UploadEntry[] =>
  (docs ?? [])
    .filter((doc) => Boolean(doc?.url))
    .map((doc, idx) => {
      const docWithOptionalMetadata = doc as typeof doc & {
        specify?: string;
        fileformat?: string;
        fileFormat?: string;
        issueDate?: string | Date | null;
        expiryDate?: string | Date | null;
        summary?: string;
      };

      return {
        url: doc.url,
        certificateType: doc.type ?? fallbackType,
        name: doc.originalFileName ?? doc.fileName ?? doc.name ?? `${fallbackType} ${idx + 1}`,
        documentType: doc.type ?? "",
        otherSpecifyType: docWithOptionalMetadata.specify ?? "",
        documentName: doc.name ?? "",
        type: docWithOptionalMetadata.fileformat ?? docWithOptionalMetadata.fileFormat ?? "",
        issueDate: docWithOptionalMetadata.issueDate ?? null,
        expiryDate: docWithOptionalMetadata.expiryDate ?? null,
        documentSummary: docWithOptionalMetadata.summary ?? "",
        idx,
      };
    });

const mapAdditionalResources = (
  resources: MovementServiceDetailsBackendResponse["additionalResources"] = [],
) =>
  (resources ?? []).map((resource) => ({
    additionalResourceType: resource.additionalResourceType ?? "",
    numberOfResources: resource.numberOfResources ? String(resource.numberOfResources) : "",
  }));

const buildFormValuesFromDetails = (
  details: MovementServiceDetailsBackendResponse,
): MovementServiceFormData => {
  const additionalDocuments = mapDocumentsToFormEntries(
    details.additionalDocuments,
    DOCUMENT_TYPE_FALLBACKS.additionalDocuments,
  );
  const pilotRequestNotification = mapDocumentsToFormEntries(
    details.pilotRequestNotification,
    DOCUMENT_TYPE_FALLBACKS.pilotRequestNotification,
  );
  const h2s = mapDocumentsToFormEntries(details.h2s, DOCUMENT_TYPE_FALLBACKS.h2s);

  return {
    ...movementServiceFormDefaultValues,
    from: details.from ?? "",
    fromAreaBerth: details.fromAreaBerth ?? "",
    to: details.to ?? "",
    toAreaBerth: details.toAreaBerth ?? "",
    preferredAnchorageArea: details.preferredAnchorageArea ?? "",
    anchoragePreferenceReason: details.anchoragePreferenceReason ?? "",
    rts: details.rts ? new Date(details.rts) : movementServiceFormDefaultValues.rts,
    ets: details.ets ? new Date(details.ets) : movementServiceFormDefaultValues.ets,
    vesselCondition: details.vesselCondition ?? "",
    movementType: details.movementType ?? "",
    movementReason: details.movementReason ?? "",
    draftFore:
      typeof details.draftFore === "number" ? String(details.draftFore) : (details.draftFore ?? ""),
    draftAft:
      typeof details.draftAft === "number" ? String(details.draftAft) : (details.draftAft ?? ""),
    hasOilStains: typeof details.hasOilStains === "boolean" ? details.hasOilStains : null,
    hasProtrusionsOrOverhand:
      typeof details.hasProtrusionsOrOverhand === "boolean"
        ? details.hasProtrusionsOrOverhand
        : null,
    additionalResources: mapAdditionalResources(details.additionalResources),
    additionalDocuments,
    pilotRequestNotification,
    h2s,
    comments: details.comments ?? "",
    vesselId: details.vesselId ?? movementServiceFormDefaultValues.vesselId,
    vesselCallId: details.vesselCallId ?? movementServiceFormDefaultValues.vesselCallId,
    associatedPortServiceId: details.associatedPortService?.portServiceId ?? "",
  };
};

const AddMovementService: React.FC = () => {
  const {
    formData,
    updateFormData,
    handleSubmit,
    setAll,
    cancel,
    vesselId,
    vesselCallId,
    draftAft,
    draftFwd,
    pastMovementsData,
    isPastMovementsLoading,
    resetPersistedForm,
    currentMovements,
    isMovementSubmitting,
    selectPortReferenceCode,
  } = useOutletContext<MovementOutletContext>();

  const { t } = useTranslation();
  const { notify } = useNotification();
  const { userActionPermissions } = useAuthStore();
  const canRequestApprovedMovement = checkPermission(
    userActionPermissions ?? [],
    "canRequestApprovedMovementService",
  );

  const pastMovement = useMemo(() => {
    return pastMovementsData?.items?.find((movement) => movement.status === "CLOSED");
  }, [pastMovementsData]);
  const hasClosedSeaPastMovement = useMemo(() => {
    return (
      pastMovementsData?.items?.some(
        (movement) => movement.from === DEFAULT_FROM_VALUE && movement.status === "CLOSED",
      ) ?? false
    );
  }, [pastMovementsData]);

  const pastMovementToLocation = pastMovement?.to ?? undefined;

  const { movementServiceResubmit, setMovementServiceResubmit } = useAuthStore();
  const currentMovement = currentMovements?.[0];

  const fromValue = useMovementServiceFormStore((state) => state.from);
  const fromLabelForValue = useMovementServiceFormStore((state) => state.fromLabelForValue);
  const toValue = useMovementServiceFormStore((state) => state.to);
  const currentMovementType = useMovementServiceFormStore((state) => state.movementType);
  const storedVesselId = useMovementServiceFormStore((state) => state.vesselId);
  const storedVesselCallId = useMovementServiceFormStore((state) => state.vesselCallId);
  const setField = useMovementServiceFormStore((state) => state.setField);
  const getField = useMovementServiceFormStore((state) => state.getField);
  const resetFieldsExcept = useMovementServiceFormStore((state) => state.resetFieldsExcept);
  const fromAndToHaveValues = fromValue !== "" && toValue !== "";
  const isFromSea = fromValue === "SEA";

  const isMarineRequiredFieldsMovement = useMemo(() => {
    return canRequestApprovedMovement && isMarineRequiredFieldsMovementType(currentMovementType);
  }, [currentMovementType, canRequestApprovedMovement]);

  const { options: fromToLocations = [], isLoading: isFromToLocationsLoading } =
    useFormattedReference<FromToLocation>(
      REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
      "bff-vessel-call",
    );

  const filteredFromToLocations = useMemo(
    () =>
      fromToLocations.filter(
        ({ portCode }) =>
          portCode?.toLowerCase() === selectPortReferenceCode?.toLowerCase() || portCode === null,
      ),
    [fromToLocations, selectPortReferenceCode],
  );

  const locations: MovementLocationOption[] = useMemo(
    () => mapFromToLocationsToMovementOptions(filteredFromToLocations),
    [filteredFromToLocations],
  );

  const autocompleteValueForFrom = useMemo(() => {
    if (fromValue !== "") {
      return locations.find((location) => location.value === fromValue);
    }
    if (!pastMovementToLocation) {
      return undefined;
    }
    return locations.find((location) => location.value === pastMovementToLocation);
  }, [fromValue, pastMovementToLocation, locations]);

  const TO_ANCHORAGE_MOVEMENT_TYPES = [
    "SEA_TO_ANCHORAGE",
    "BERTH_TO_ANCHORAGE",
    "ANCHORAGE_TO_ANCHORAGE",
  ];

  const fromLabelToDisplay = autocompleteValueForFrom?.label ?? fromValue;
  const fromAreaBerthDisplayValue = TO_ANCHORAGE_MOVEMENT_TYPES.includes(
    pastMovement?.movementType ?? "",
  )
    ? pastMovement?.location
    : pastMovement?.toAreaBerth;

  const { data: anchorageAreas } = useQuery({
    queryKey: ["anchorageAreas"],
    queryFn: () => getAnchorageAreas("bff-vessel-call"),
  });

  const anchorageOptions: { value: string; label: string }[] = Array.isArray(anchorageAreas)
    ? anchorageAreas
        .filter((option) => option.fromtoloc?.toLowerCase() === toValue.toLowerCase())
        .map((option) => ({
          value: option.anchorageAreaCode,
          label: option.anchorageAreaDesc,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const hasInitializedFromValue = useRef(fromValue !== "");
  const hydratedMovementIdRef = useRef<string | null>(null);
  const toLocationOptions = useMemo(
    () =>
      fromLabelForValue === "HIGH SEAS"
        ? locations.filter((loc) => loc.label !== "HIGH SEAS")
        : locations,
    [locations, fromLabelForValue],
  );
  useEffect(() => {
    return () => {
      setMovementServiceResubmit(null);
      hydratedMovementIdRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (movementServiceResubmit) {
      return;
    }
    if (hasInitializedFromValue.current) {
      return;
    }
    if (fromValue !== "") {
      hasInitializedFromValue.current = true;
      return;
    }
    if (isFromToLocationsLoading || isPastMovementsLoading) {
      return;
    }
    const initialFromValue = autocompleteValueForFrom?.value ?? DEFAULT_FROM_VALUE;
    setField("from", initialFromValue);
    setField("fromAreaBerth", fromAreaBerthDisplayValue ?? "");
    formData.setValue("fromAreaBerth", fromAreaBerthDisplayValue ?? "", {
      shouldDirty: false,
      shouldValidate: false,
    });
    hasInitializedFromValue.current = true;
  }, [
    autocompleteValueForFrom,
    formData,
    fromValue,
    movementServiceResubmit,
    isFromToLocationsLoading,
    isPastMovementsLoading,
    setField,
    fromAreaBerthDisplayValue,
  ]);

  const movementTypesWithoutPilotNotification = [
    "SEA_TO_ANCHORAGE",
    "ANCHORAGE_TO_ANCHORAGE",
    "ANCHORAGE_TO_SEA",
  ];

  const movementTypesWithH2S = ["SEA_TO_BERTH", "ANCHORAGE_TO_BERTH", "BERTH_TO_BERTH"];

  const movementSeaToBerth = currentMovementType === "SEA_TO_BERTH";
  const movementBerthToSea = currentMovementType === "BERTH_TO_SEA";
  const movementAnchorageToSea = currentMovementType === "ANCHORAGE_TO_SEA";
  const movementAnchorageToBerth = currentMovementType === "ANCHORAGE_TO_BERTH";
  const movementSeaToAnchorage = currentMovementType === "SEA_TO_ANCHORAGE";
  const movementBerthToBerth = currentMovementType === "BERTH_TO_BERTH";
  const movementBerthToAnchorage = currentMovementType === "BERTH_TO_ANCHORAGE";
  const movementAnchorageToAnchorage = currentMovementType === "ANCHORAGE_TO_ANCHORAGE";

  const fromAreaBerthLabel =
    movementAnchorageToSea || movementAnchorageToBerth
      ? t(`${LANG_BASE}.fields.from-location`)
      : movementBerthToAnchorage
        ? t(`${LANG_BASE}.fields.from-berth`)
        : t(`${LANG_BASE}.fields.from-area-berth`);

  const showPilotNotification =
    !movementTypesWithoutPilotNotification.includes(currentMovementType);
  const showH2S =
    movementTypesWithH2S.includes(currentMovementType) && H2S_VISIBLE_DESTINATIONS.has(toValue);

  useEffect(() => {
    const nextFromLabelForValue = fromValue === "" ? "" : fromLabelToDisplay;

    if (fromLabelForValue !== nextFromLabelForValue) {
      setField("fromLabelForValue", nextFromLabelForValue);
    }
    if (formData.getValues("fromLabelForValue") !== nextFromLabelForValue) {
      formData.setValue("fromLabelForValue", nextFromLabelForValue, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [formData, fromLabelForValue, fromLabelToDisplay, fromValue, setField]);

  useEffect(() => {
    if (vesselId && vesselId !== storedVesselId) {
      setField("vesselId", vesselId);
    }
    if (vesselCallId && vesselCallId !== storedVesselCallId) {
      setField("vesselCallId", vesselCallId);
    }
  }, [fromAndToHaveValues, vesselId, vesselCallId, storedVesselId, storedVesselCallId, setField]);

  useEffect(() => {
    const hasFromValue = fromValue !== "";
    const hasToValue = toValue !== "";

    if (hasFromValue && hasToValue) {
      const fromMoveType = getMoveTypeForLocation(fromValue, locations);
      const toMoveType = getMoveTypeForLocation(toValue, locations);
      const nextMovementType = createMovementType(fromMoveType, toMoveType);
      if (currentMovementType !== nextMovementType) {
        setField("movementType", nextMovementType);
        formData.setValue("movementType", nextMovementType, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    } else if (currentMovementType !== "") {
      setField("movementType", "");
      formData.setValue("movementType", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [fromValue, toValue, currentMovementType, setField, locations, formData]);

  const marineFieldsClearedRef = useRef(false);

  useEffect(() => {
    if (isMarineRequiredFieldsMovement) {
      marineFieldsClearedRef.current = false;
      return;
    }

    if (marineFieldsClearedRef.current) {
      return;
    }

    marineFieldsClearedRef.current = true;

    setField("toAreaBerth", "");
    formData.setValue("toAreaBerth", "", {
      shouldDirty: false,
      shouldValidate: false,
    });

    setField("ets", "");
    formData.setValue("ets", "", {
      shouldDirty: false,
      shouldValidate: false,
    });

    formData.clearErrors(["toAreaBerth", "ets"]);
  }, [isMarineRequiredFieldsMovement, formData, setField]);

  useEffect(() => {
    if (!fromAndToHaveValues || !currentMovementType || !isFromSea || hasClosedSeaPastMovement) {
      return;
    }

    const hydrateDraftField = (
      fieldName: "draftAft" | "draftFore",
      valueFromVesselCall: string,
    ) => {
      if (!valueFromVesselCall) {
        return;
      }

      const storeValue = getField(fieldName);
      const formValue = formData.getValues(fieldName);
      const hasValue =
        (typeof storeValue === "string" && storeValue !== "") ||
        (typeof formValue === "string" && formValue !== "");

      if (hasValue) {
        return;
      }

      setField(fieldName, valueFromVesselCall);
      formData.setValue(fieldName, valueFromVesselCall, {
        shouldDirty: false,
        shouldValidate: false,
      });
    };

    hydrateDraftField("draftAft", draftAft ? String(draftAft) : "");
    hydrateDraftField("draftFore", draftFwd ? String(draftFwd) : "");
  }, [
    currentMovementType,
    draftAft,
    draftFwd,
    formData,
    fromAndToHaveValues,
    getField,
    hasClosedSeaPastMovement,
    isFromSea,
    setField,
    toValue,
  ]);

  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const filesByKey = useUploadStore((state) => state.filesByKey);

  const isUploadInProgress = useMemo(
    () =>
      uploadFieldKeys.some((fieldKey) =>
        (filesByKey[fieldKey] ?? []).some((entry) => {
          if (entry instanceof File) {
            return true;
          }
          return Boolean(entry && typeof entry === "object" && "url" in entry && !entry.url);
        }),
      ),
    [filesByKey],
  );

  const hydrateUploadFields = useCallback(
    (values: MovementServiceFormData) => {
      uploadFieldKeys.forEach((fieldKey) => {
        const docs = values[fieldKey];
        if (Array.isArray(docs) && docs.length > 0) {
          setFilesForKey(fieldKey as string, docs);
        } else {
          clearFilesForKey(fieldKey as string);
        }
      });
    },
    [clearFilesForKey, setFilesForKey],
  );

  const populateFormFromDetails = useCallback(
    (details: MovementServiceDetailsBackendResponse) => {
      const mappedValues = buildFormValuesFromDetails(details);
      setAll(mappedValues);
      formData.reset(mappedValues);
      hydrateUploadFields(mappedValues);
    },
    [formData, hydrateUploadFields, setAll],
  );

  const { data: resubmissionDetails, isFetching: isFetchingResubmissionDetails } = useQuery({
    queryKey: ["movement-service-resubmit", currentMovement?.id],
    queryFn: async () => {
      if (!currentMovement?.id) {
        throw new Error("Movement ID is required for resubmission");
      }

      return getMovementServiceDetailsById({
        id: currentMovement.id,
        service: SERVICE,
      });
    },
    enabled: Boolean(movementServiceResubmit && currentMovement?.id),
    staleTime: 0,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (!movementServiceResubmit) {
      hydratedMovementIdRef.current = null;
    }
  }, [movementServiceResubmit]);

  useEffect(() => {
    if (
      !movementServiceResubmit ||
      isFetchingResubmissionDetails ||
      !resubmissionDetails ||
      !currentMovement?.id ||
      hydratedMovementIdRef.current === currentMovement.id
    ) {
      return;
    }

    hydratedMovementIdRef.current = currentMovement.id;
    populateFormFromDetails(resubmissionDetails);
  }, [
    currentMovement?.id,
    isFetchingResubmissionDetails,
    movementServiceResubmit,
    populateFormFromDetails,
    resubmissionDetails,
  ]);

  useEffect(() => {
    if (!resetPersistedForm) {
      return;
    }

    const vesselHasChanged =
      Boolean(storedVesselId && vesselId && storedVesselId !== vesselId) ||
      Boolean(storedVesselCallId && vesselCallId && storedVesselCallId !== vesselCallId);

    if (vesselHasChanged) {
      resetPersistedForm();
      formData.reset({
        ...movementServiceFormDefaultValues,
      });
      uploadFieldKeys.forEach((field) => clearFilesForKey(field as string));
      hasInitializedFromValue.current = false;
    }
  }, [
    vesselId,
    vesselCallId,
    storedVesselId,
    storedVesselCallId,
    resetPersistedForm,
    clearFilesForKey,
    formData,
  ]);

  const resetNonLocationFields = useCallback(() => {
    const preservedFields: (keyof MovementServiceFormData)[] = [
      "from",
      "to",
      "rts",
      "fromAreaBerth",
      "fromLabelForValue",
    ];
    const preserved = new Set(preservedFields);

    (Object.keys(movementServiceFormDefaultValues) as (keyof MovementServiceFormData)[]).forEach(
      (field) => {
        if (preserved.has(field)) {
          return;
        }
        const defaultValue = movementServiceFormDefaultValues[field];
        const valueToSet = Array.isArray(defaultValue)
          ? ([...defaultValue] as MovementServiceFormData[typeof field])
          : defaultValue;
        formData.setValue(field, valueToSet, { shouldDirty: false, shouldValidate: false });
        if (uploadFieldSet.has(field as UploadField)) {
          clearFilesForKey(field as string);
        }
      },
    );
  }, [clearFilesForKey, formData]);

  const handleToFieldChange = useCallback<MovementOutletContext["updateFormData"]>(
    (key, value) => {
      const previousValue = key === "to" ? (getField("to") as string) : undefined;

      updateFormData(key, value);

      if (key !== "to" && key !== "preferredAnchorageArea") {
        return;
      }

      const stringValue = typeof value === "string" ? value : "";
      const hasChanged = previousValue !== undefined ? previousValue !== stringValue : true;

      if (stringValue === "" || hasChanged) {
        if (key === "preferredAnchorageArea") {
          formData.clearErrors("anchoragePreferenceReason");
          return;
        }
        resetFieldsExcept(["from", "to", "rts", "fromAreaBerth", "fromLabelForValue"]);
        resetNonLocationFields();
      }
    },
    [formData, getField, resetFieldsExcept, resetNonLocationFields, updateFormData],
  );

  const ownersMatter = useCheckOwnersMatter(vesselCallId || "");

  const handleComplete = useCallback(() => {
    if (isUploadInProgress) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    handleSubmit();
  }, [handleSubmit, isUploadInProgress]);

  const submitButtonLabel = useMemo(() => {
    if (canRequestApprovedMovement) {
      return "stepper-controls.submit-movement-service";
    }
    return "stepper-controls.request-movement-service";
  }, [canRequestApprovedMovement]);

  if (ownersMatter) {
    return <UnauthorizedRedirection />;
  }

  return (
    <div data-testid="movement-service-form" className="flex flex-col gap-6">
      <Typography
        variant="h5"
        data-testid="movement-service-form-title"
        sx={{ color: "text.primary", ml: 1 }}
      >
        {t(`${LANG_BASE}.add-service`)}
      </Typography>

      <FormProvider {...formData}>
        <div className="flex flex-col gap-8">
          <FormSection title={t(`${LANG_BASE}.sections.movement`)}>
            <FormItem<MovementServiceFormData, "fromLabelForValue">
              name="fromLabelForValue"
              type="text"
              label={t(`${LANG_BASE}.fields.from`)}
              testId="movement-from"
              onFieldChange={handleToFieldChange}
              disabled
            />
            <FormItem<MovementServiceFormData, "to">
              name="to"
              type="autocomplete"
              label={t(`${LANG_BASE}.fields.to`)}
              testId="movement-to"
              onFieldChange={handleToFieldChange}
              options={toLocationOptions}
              disabled={!!movementServiceResubmit}
            />
            {toValue !== "" && (
              <>
                {movementBerthToSea || movementBerthToBerth ? (
                  <>
                    <FormItem<MovementServiceFormData, "fromAreaBerth">
                      name="fromAreaBerth"
                      type="text"
                      label={
                        movementAnchorageToSea
                          ? t(`${LANG_BASE}.fields.from-location`)
                          : t(`${LANG_BASE}.fields.from-berth`)
                      }
                      testId="movement-fromAreaBerth"
                      onFieldChange={handleToFieldChange}
                      disabled
                    />
                  </>
                ) : (
                  <>
                    {!movementSeaToBerth && !movementSeaToAnchorage && (
                      <>
                        <FormItem<MovementServiceFormData, "fromAreaBerth">
                          name="fromAreaBerth"
                          type="text"
                          label={
                            movementAnchorageToAnchorage
                              ? t(`${LANG_BASE}.fields.from-location`)
                              : fromAreaBerthLabel
                          }
                          testId="movement-fromAreaBerth"
                          onFieldChange={handleToFieldChange}
                          disabled
                        />
                        {movementBerthToAnchorage ? (
                          <>
                            <FormItem<MovementServiceFormData, "preferredAnchorageArea">
                              name="preferredAnchorageArea"
                              type="autocomplete"
                              options={anchorageOptions}
                              label={t(`${LANG_BASE}.fields.preferred-anchorage-area`)}
                              testId="movement-preferredAnchorageArea"
                              onFieldChange={handleToFieldChange}
                            />
                            <div className="col-span-2">
                              <FormItem<MovementServiceFormData, "anchoragePreferenceReason">
                                name="anchoragePreferenceReason"
                                type="text"
                                label={t(`${LANG_BASE}.fields.reason-for-anchorage-preference`)}
                                testId="movement-anchoragePreferenceReason"
                                onFieldChange={handleToFieldChange}
                                maxChars={100}
                              />
                            </div>
                          </>
                        ) : movementAnchorageToSea ||
                          movementAnchorageToBerth ||
                          movementAnchorageToAnchorage ? null : isMarineRequiredFieldsMovement ? (
                          <FormItem<MovementServiceFormData, "toAreaBerth">
                            name="toAreaBerth"
                            type="autocomplete"
                            options={anchorageOptions}
                            label={t(`${LANG_BASE}.fields.to-area-berth`)}
                            testId="movement-toAreaBerth"
                            onFieldChange={handleToFieldChange}
                          />
                        ) : (
                          <FormItem<MovementServiceFormData, "toAreaBerth">
                            name="toAreaBerth"
                            type="text"
                            label={t(`${LANG_BASE}.fields.to-area-berth`)}
                            testId="movement-toAreaBerth"
                            onFieldChange={handleToFieldChange}
                            disabled
                          />
                        )}
                      </>
                    )}
                    {movementAnchorageToAnchorage && (
                      <>
                        <FormItem<MovementServiceFormData, "preferredAnchorageArea">
                          name="preferredAnchorageArea"
                          type="autocomplete"
                          options={anchorageOptions}
                          label={t(`${LANG_BASE}.fields.preferred-anchorage-area`)}
                          testId="movement-preferredAnchorageArea"
                          onFieldChange={handleToFieldChange}
                        />
                        <div className="col-span-full">
                          <FormItem<MovementServiceFormData, "anchoragePreferenceReason">
                            name="anchoragePreferenceReason"
                            type="text"
                            label={t(`${LANG_BASE}.fields.reason-for-anchorage-preference`)}
                            testId="movement-anchoragePreferenceReason"
                            onFieldChange={updateFormData}
                            maxChars={100}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="md:col-start-1">
                  <FormItem<MovementServiceFormData, "rts">
                    name="rts"
                    type="date-picker"
                    label={t(`${LANG_BASE}.fields.rts`)}
                    testId="movement-rts"
                    onFieldChange={updateFormData}
                    timePicker
                    combinedInput
                    dateConstraint="not-past"
                  />
                </div>
                {isMarineRequiredFieldsMovement && (
                  <FormItem<MovementServiceFormData, "ets">
                    name="ets"
                    type="date-picker"
                    label={t(`${LANG_BASE}.fields.ets`)}
                    testId="movement-ets"
                    onFieldChange={updateFormData}
                    timePicker
                    combinedInput
                    dateConstraint="not-past"
                    minDate={(formData.getValues("rts") as Date) || undefined}
                  />
                )}
              </>
            )}
          </FormSection>
        </div>
        {fromAndToHaveValues && (
          <>
            <MovementVesselDetails
              hasClosedSeaPastMovement={hasClosedSeaPastMovement}
              vesselCallId={vesselCallId}
            />
            <AdditionalResources key={toValue} />
            {showPilotNotification && <PilotRequestNotification />}
            {showH2S && <H2S />}
            <FormItem<MovementServiceFormData, "comments">
              name="comments"
              type="textarea"
              label={t(`${LANG_BASE}.fields.comments`)}
              testId="movement-comments"
              onFieldChange={updateFormData}
              maxChars={MOVEMENT_SERVICE_REMARKS_MAX_CHARS}
              minRows={3}
              showCharCounter
            />
            <MovementAdditionalDocuments />
            <div className="py-6 border-t border-t-[var(--stroke-secondary-stroke,#E6EDF2)]">
              <StepperControls
                cancel={cancel}
                complete={handleComplete}
                completePending={isMovementSubmitting}
                completeLabelTranslationKey={submitButtonLabel}
              />
            </div>
          </>
        )}
      </FormProvider>
    </div>
  );
};

export default AddMovementService;
